import React, { useState } from "react";
import { popsApi, fmtUSD } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";

export default function CspTab({ config, onChange }) {
  const [edits, setEdits] = useState({});

  const save = async (sub) => {
    const seats = parseInt(edits[sub.id]);
    if (isNaN(seats) || seats < 0) {
      toast.error("Enter a valid seat count");
      return;
    }
    try {
      await popsApi.setSeats(sub.id, seats);
      toast.success(`${sub.sku}: seats updated to ${seats} — next invoice run picks it up`);
      setEdits({ ...edits, [sub.id]: undefined });
      onChange();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const totalRevenue = config.csp_subscriptions.reduce((s, x) => s + x.seats * x.unit_price, 0);
  const totalCost = config.csp_subscriptions.reduce((s, x) => s + x.seats * x.unit_cost, 0);

  return (
    <div className="space-y-4" data-testid="pops-csp-tab">
      <div className="grid grid-cols-3 gap-4">
        <div className="cs-card p-4"><div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Monthly seat revenue</div>
          <div className="cs-kpi-value text-xl text-[#00579e] mt-1" data-testid="csp-revenue">{fmtUSD(totalRevenue)}</div></div>
        <div className="cs-card p-4"><div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Publisher cost</div>
          <div className="cs-kpi-value text-xl mt-1">{fmtUSD(totalCost)}</div></div>
        <div className="cs-card p-4"><div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Seat margin</div>
          <div className="cs-kpi-value text-xl text-[#15803d] mt-1">{fmtUSD(totalRevenue - totalCost)} <span className="text-xs text-muted-foreground">({((totalRevenue - totalCost) / totalRevenue * 100).toFixed(1)}%)</span></div></div>
      </div>

      <div className="cs-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <MonitorSmartphone className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">CSP & Marketplace Subscriptions (seat reselling)</h3>
          <span className="text-[11px] text-muted-foreground ml-auto">seat lines land on the same consolidated invoice as cloud usage</span>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["SKU", "Publisher", "Customer", "Seats", "Unit cost", "Unit price", "Monthly margin", "Term", "Renewal", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {config.csp_subscriptions.map((s) => {
              const expired = s.renewal && s.renewal < today;
              return (
                <tr key={s.id} className="border-b border-border last:border-0" data-testid={`csp-row-${s.id}`}>
                  <td className="px-3 py-2 font-semibold">{s.sku}</td>
                  <td className="px-3 py-2 text-muted-foreground">{s.publisher}</td>
                  <td className="px-3 py-2">{(config.customers.find((c) => c.id === s.customer_id) || {}).name}</td>
                  <td className="px-3 py-2">
                    <Input
                      type="number" defaultValue={s.seats}
                      onChange={(e) => setEdits({ ...edits, [s.id]: e.target.value })}
                      data-testid={`seats-input-${s.id}`}
                      className="rounded-sm h-7 w-[80px] text-xs cs-mono"
                    />
                  </td>
                  <td className="px-3 py-2 cs-mono">{fmtUSD(s.unit_cost)}</td>
                  <td className="px-3 py-2 cs-mono">{fmtUSD(s.unit_price)}</td>
                  <td className="px-3 py-2 cs-mono font-semibold text-[#15803d]">{fmtUSD(s.seats * (s.unit_price - s.unit_cost))}</td>
                  <td className="px-3 py-2">{s.term}{s.co_term ? " · co-term" : ""}</td>
                  <td className="px-3 py-2">
                    <span className={`cs-mono ${expired ? "text-[#b91c1c] font-semibold" : "text-muted-foreground"}`}>
                      {s.renewal}{expired ? " ⚠ past renewal" : ""}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {edits[s.id] !== undefined && (
                      <Button size="sm" onClick={() => save(s)} data-testid={`save-seats-${s.id}`}
                        className="rounded-full h-6 text-[10px] px-3 bg-[#00579e] hover:bg-[#004580] text-white">Save</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
